import {
  type BoardColumn,
  BoardProvider,
  type BoardProviderProps,
  type BoardRow,
  type CellAddress,
  type ColumnKey,
  type RowKey,
  useBoardData,
  useBoardLabels,
  useBoardOverviewHeadingLevel,
  useBoardRoot,
} from '@github-ui/board-core'
import {useResizeObserver} from '@github-ui/use-resize-observer'
import {useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type ReactNode, useEffect, useMemo, useRef} from 'react'

import styles from './Board.module.css'
import headingStyles from './BoardHeadings.module.css'
import {BoardCellFrame} from './frames/BoardCellFrame'
import {BoardColumnFrame} from './frames/BoardColumnFrame'
import {BoardDraggedCard} from './frames/BoardDraggedCard'
import {BoardColumnPaging, BoardRowPaging} from './frames/BoardPaging'
import {BoardRowFrame} from './frames/BoardRowFrame'
import {BoardCellSkeleton} from './frames/BoardSkeletons'
import {BoardMetricsProbes, BoardWindowSpacer} from './window/BoardWindow'
import {BoardWindowFocus} from './window/BoardWindowFocus'
import {useBoardWindow} from './window/use-board-window'

export type BoardBodyProps<TItem> = {
  /** A board cannot know what a card is, so the caller draws one. */
  renderCard: (item: TItem) => ReactNode
  /** A column is a milestone, a label or a status, so its header shows different things. */
  renderColumnHeader: (column: BoardColumn) => ReactNode
  /**
   * Optional, where the column one is not. A board with no swimlane is the one-row case
   * of the same structure, and its single row has nothing worth naming, so a caller
   * that passes nothing here gets no row chrome, and the board looks exactly as it did
   * before swimlanes existed.
   *
   * **Whether the board has row headers is stated on the provider**, as `hasRowHeaders`,
   * and this is only how one is drawn. `<Board>` states it from this prop, so its callers
   * have one place; a caller composing the two states it itself, and a board whose two
   * answers disagree says so on the console outside production.
   */
  renderRowHeader?: (row: BoardRow) => ReactNode
  /**
   * What an empty cell says. Falls back to the `emptyCell` label when it is not
   * supplied, but a renderer that returns `null` is taken at its word and draws
   * nothing, so a caller can suppress the empty state per cell.
   *
   * **Returning `null` costs a reader something.** An empty cell is where the board's
   * keyboard focus lands, and the element that takes it is the cell's scroll container,
   * which has no name of its own because the cell around it is already a named group.
   * The empty-state text is what a screen reader reads on arriving there, so a cell that
   * draws nothing is a cell a reader arrives at in silence.
   */
  renderEmptyCell?: (cell: CellAddress) => ReactNode
  /**
   * How tall every row is, as a CSS length. Defaults to `100%`, which is the whole of
   * the board's scroll viewport.
   *
   * **A swimlaned board should set this**, to something like `24rem`. Rows are a fixed
   * height, not a content-sized one: `100%` gives one row exactly the visible band, and
   * several rows a board that scrolls downwards through bands of equal depth. Each cell
   * scrolls its own cards inside its band.
   *
   * It is one rule for every row, whether the board has one or twelve, and the board
   * never measures a row from what is in it. A row sized by its contents would make the
   * row window estimate heights that only settle after each row has rendered, and would
   * leave a cell that never scrolls reporting a viewport as tall as everything in it.
   */
  rowHeight?: string
  className?: string
}

export type BoardProps<TItem> = Omit<BoardProviderProps<TItem>, 'children' | 'hasRowHeaders'> & BoardBodyProps<TItem>

/**
 * A board: columns across the top, swimlane rows down the side, and a cell where each
 * pair meets.
 *
 * Only the columns and rows on screen are drawn, so a board fifty columns wide costs
 * what fits rather than what it holds. It reserves the whole of each axis either way, so
 * the scrollbars still describe the whole board.
 *
 * It is markup, CSS and calls into `@github-ui/board-core`'s hooks. It adds no
 * behaviour of its own, so a caller who wants a different appearance can drop to the
 * core and keep everything the board does.
 *
 * **To put your own controls beside it**, render `BoardProvider` and {@link BoardBody}
 * yourself, which is exactly what this composes. That is the only way a control of yours
 * can read the board's state, because `useBoardSelection` and the rest answer inside the
 * provider and this renders its own.
 */ export function Board<TItem>({
  renderCard,
  renderColumnHeader,
  renderRowHeader,
  renderEmptyCell,
  rowHeight,
  className,
  ...providerProps
}: BoardProps<TItem>) {
  return (
    <BoardProvider {...providerProps} hasRowHeaders={renderRowHeader !== undefined}>
      <BoardBody
        renderCard={renderCard}
        renderColumnHeader={renderColumnHeader}
        renderRowHeader={renderRowHeader}
        renderEmptyCell={renderEmptyCell}
        rowHeight={rowHeight}
        className={className}
      />
    </BoardProvider>
  )
}

const NO_ITEMS: readonly never[] = []

function BoardBodyImpl<TItem>({
  renderCard,
  renderColumnHeader,
  renderRowHeader,
  renderEmptyCell,
  rowHeight,
  className,
}: BoardBodyProps<TItem>) {
  const {rootProps, hasUnboundedHeight, hasRowHeaders, scrollRef} = useBoardRoot()
  const labels = useBoardLabels()
  const overviewHeadingLevel = useBoardOverviewHeadingLevel() ?? 2
  const OverviewHeading = `h${overviewHeadingLevel}` as const
  const {columns, rows, cells, isLoadingColumns, columnsTruncated} = useBoardData<TItem>()
  const boardRef = useRef<HTMLElement>(null)

  // Only the columns and rows on screen are drawn. Two windows over the one scroll
  // container the board already owns, and the column one is used twice: once in the
  // sticky header strip, and once inside every rendered row.
  const {
    columns: columnWindow,
    rows: rowWindow,
    columnHeadersRef,
    rowsRef,
    columnProbeRef,
    rowProbeRef,
  } = useBoardWindow({
    scrollRef,
    columnCount: columns.length,
    rowCount: rows.length,
    hasUnboundedHeight,
  })
  const visibleColumnKeys = keysAt(columns, columnWindow.indexes)
  const visibleRowKeys = keysAt(rows, rowWindow.indexes)

  // The header strip's height follows its content: a column name wraps rather than
  // truncating, the caller draws the header itself, and both grow with the reader's
  // text size. A fixed reservation would be too small in exactly those cases and let a
  // focused card scroll underneath the strip, so the rendered height is measured and
  // written back as the board's scroll padding. A custom property rather than state, so
  // a resize restyles the board without re-rendering it.
  const measureColumnHeaders = useResizeObserver(entry => {
    const blockSize = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height
    boardRef.current?.style.setProperty('--board-column-headers-block-size', `${blockSize}px`)
  })
  const mergedColumnHeadersRef = useMergedRefs(measureColumnHeaders, columnHeadersRef)
  const mergedBoardRef = useMergedRefs(rootProps.ref, boardRef)

  // Reading a cell's items is the one thing that needs the item type, so it happens
  // here rather than in `useBoardCell`. Built once per render instead of searching
  // `cells` per cell, which would be quadratic in the size of the board.
  //
  // Nested rather than keyed on the two halves joined into one string: keys are opaque
  // and may hold any character, so every separator collides on some pair of keys, and a
  // collision here would pair one cell's items with another cell's ids.
  const itemsByColumnThenRow = useMemo(() => {
    const lookup = new Map<ColumnKey, Map<RowKey, readonly TItem[]>>()
    for (const cell of cells) {
      let byRow = lookup.get(cell.columnKey)
      if (byRow === undefined) {
        byRow = new Map()
        lookup.set(cell.columnKey, byRow)
      }

      byRow.set(cell.rowKey, cell.items)
    }

    return lookup
  }, [cells])

  return (
    <section
      {...rootProps}
      ref={mergedBoardRef}
      // Drives whether a column's header joins onto the cells below it. A swimlaned
      // board puts a row header between the two, so there is nothing adjacent to join.
      //
      // The fact the caller stated, not `renderRowHeader !== undefined`, which derives it
      // from the drawing of a header. It is the same fact a cell is named with, so a board
      // that looks swimlaned and a cell that says it is cannot disagree.
      data-row-headers={hasRowHeaders}
      // The failure below, left on the element so it can be read from a page that is
      // already wrong rather than only from a console nobody had open.
      data-unbounded-height={hasUnboundedHeight}
      style={rowHeight === undefined ? rootProps.style : {...rootProps.style, '--board-row-block-size': rowHeight}}
      className={clsx(styles.Board, className)}
    >
      {/* Renders nothing. Mounted on the failure so the report follows the crossing. */}
      {hasUnboundedHeight && REPORTS_TO_THE_DEVELOPER ? <BoardUnboundedHeightReport /> : null}

      {/* Renders nothing either, and mounted on its own failure for the same reason. */}
      {hasRowHeaders !== (renderRowHeader !== undefined) && REPORTS_TO_THE_DEVELOPER ? (
        <BoardRowHeaderMismatchReport hasRowHeaders={hasRowHeaders} />
      ) : null}

      {/*
        Renders nothing either. It brings a column or a row that focus is owed to back into
        the window, which is the one thing a scroll offset cannot be asked for: the cell is
        not in the page, so there is no element to focus and none to scroll to.
      */}
      <BoardWindowFocus columns={columnWindow} rows={rowWindow} />

      {/*
        Draws nothing here either: the copy of a held card that follows the pointer goes
        through a portal, so it is neither inside the board's scroll container nor beside
        the board in the box a caller put it in.
      */}
      <BoardDraggedCard renderCard={renderCard} />

      <div ref={mergedColumnHeadersRef} className={styles.ColumnHeaders}>
        <OverviewHeading className={headingStyles.HiddenHeading}>{labels.columnOverview}</OverviewHeading>
        <BoardWindowSpacer axis="inline" size={columnWindow.leadingSize} />
        {columnWindow.indexes.map(index => {
          const column = columns[index]

          return column === undefined ? null : (
            <BoardColumnFrame key={column.key} columnKey={column.key}>
              {renderColumnHeader(column)}
            </BoardColumnFrame>
          )
        })}
        <BoardWindowSpacer axis="inline" size={columnWindow.trailingSize} />
        {/*
          The column axis's own paging, at the end of the columns and inside the sticky
          strip, which is where a column that has not arrived yet would appear. It pages
          no row and no cell. It passes the drawn rows to `onLoadMoreColumns` so the
          caller can load cards for the cells a reader will see.

          One ghost for the whole axis rather than one per column, so the window never
          leaves it out: it is a sibling of the reserved extent rather than an item
          inside it, which puts it at the end of the whole axis exactly as it was before
          the board windowed anything.
        */}
        <BoardColumnPaging visibleRowKeys={visibleRowKeys} />
      </div>

      <div ref={rowsRef} className={styles.Rows}>
        <BoardWindowSpacer axis="block" size={rowWindow.leadingSize} />
        {rowWindow.indexes.map(index => {
          const row = rows[index]

          return row === undefined ? null : (
            <BoardRowFrame key={row.key} rowKey={row.key} header={renderRowHeader?.(row)}>
              {/*
                The same window as the header strip above, not a second one that agrees
                with it: two windows would only have to disagree once to put a header
                over the wrong cell.
              */}
              <BoardWindowSpacer axis="inline" size={columnWindow.leadingSize} />
              {columnWindow.indexes.map(columnIndex => {
                const column = columns[columnIndex]

                return column === undefined ? null : (
                  <BoardCellFrame
                    key={column.key}
                    address={{columnKey: column.key, rowKey: row.key}}
                    items={itemsByColumnThenRow.get(column.key)?.get(row.key) ?? NO_ITEMS}
                    renderCard={renderCard}
                    renderEmptyCell={renderEmptyCell}
                  />
                )
              })}
              <BoardWindowSpacer axis="inline" size={columnWindow.trailingSize} />
              {isLoadingColumns === true && columnsTruncated !== true ? <BoardCellSkeleton /> : null}
            </BoardRowFrame>
          )
        })}
        <BoardWindowSpacer axis="block" size={rowWindow.trailingSize} />
        {/*
          The row axis's own paging, below the last swimlane. Independent of the column
          axis above: a board may be fetching both at once, and neither waits for the
          other. One ghost for the axis, after the reserved extent, for the same reason
          the column one is.

          It takes the same column window the rows above use, or the swimlane it draws
          while one is loading stands off the side of the board rather than under the
          columns on screen.
        */}
        <BoardRowPaging
          visibleColumnKeys={visibleColumnKeys}
          columnLeadingSize={columnWindow.leadingSize}
          columnTrailingSize={columnWindow.trailingSize}
        />
        <BoardMetricsProbes columnRef={columnProbeRef} rowRef={rowProbeRef} />
      </div>
    </section>
  )
}

function keysAt(values: ReadonlyArray<{key: string}>, indexes: readonly number[]): string[] {
  const keys: string[] = []
  for (const index of indexes) {
    const value = values[index]
    if (value !== undefined) keys.push(value.key)
  }
  return keys
}

/**
 * The board's markup and CSS, without the provider around it: everything `<Board>` draws.
 *
 * Render it inside a `BoardProvider` and your own controls become siblings of it, which
 * is the only place a control of yours can call `useBoardSelection` or any of the board's
 * other hooks. `<Board>` is this and a provider, so the two shapes cannot drift.
 *
 * ```tsx
 * <BoardProvider {...board} hasRowHeaders>
 *   <MyToolbar />
 *   <BoardBody<Issue> renderCard={renderCard} renderColumnHeader={renderColumnHeader} renderRowHeader={renderRowHeader} />
 * </BoardProvider>
 * ```
 *
 * **Name your item type on the call.** `<Board>` infers it from `data`, which this does
 * not take, so an inline `renderCard` here has nothing to infer from. `BoardBody<Issue>`
 * is where it goes, the same place `useBoardData<Issue>()` takes it.
 *
 * **The provider draws nothing**, so this and your controls land in whatever box you put
 * the provider in, and that box owns the height. The board fills its parent and owns its
 * scroll containers, so give the box `display: grid` with `grid-template-rows: auto
 * minmax(0, 1fr)`: a plain `1fr` refuses to shrink below its content, and the board would
 * overflow, losing its scrollbars, its auto-scroll, its paging and all three windows at
 * once. The unbounded-height diagnostic reports this only after the board grows taller than the viewport.
 *
 * **Your controls stay outside the board's own element**, which is what makes this the
 * shape to reach for. That element is a named region and one roving-tabindex composite
 * whose `onKeyDown` owns the arrow keys, `Home` and `End`, so a toolbar inside it would be
 * announced as part of the board and would fight it for the same keys.
 *
 * Memoised, because a selection is the caller's own state: the caller re-renders on every
 * gesture, and the board would otherwise be re-rendered from above with it. It takes none
 * of that state, and the board's data reaches it through context rather than through these
 * props, so the boundary costs it nothing. A caller the React Compiler compiled gets the
 * same bailout from the compiler first; this is for one it did not.
 */
export const BoardBody = memo(BoardBodyImpl) as typeof BoardBodyImpl

/**
 * Reports a board that has grown past the window, so the page is scrolling instead of
 * the board.
 *
 * Outside production only, and to the console rather than to the page. The failure
 * belongs to whoever is building the page around the board: the message names their
 * mistake and instructs them to fix it, so a reader using GitHub is not its audience and
 * could do nothing with it. It and the row-header report below are the only strings these
 * packages hard-code, and the only two nobody translates, because nobody is meant to be
 * shown them.
 *
 * The failure itself is not development-only. The board still declines to window a
 * container that never scrolls, and `data-unbounded-height` is on the root in every
 * environment, so a live page can still be asked.
 *
 * `console.error` rather than `warn`, because nothing here is advisory: every one of the
 * board's scroll behaviours is already broken by the time this runs.
 *
 * Reported per crossing rather than once per board, which is what mounting it on the
 * state gives: a measured state can be entered, left and entered again by resizing, and
 * silence after the first crossing would read as the page having been fixed.
 */
function BoardUnboundedHeightReport() {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error(UNBOUNDED_HEIGHT_MESSAGE)
  }, [])

  return null
}

/**
 * Reports a board told one thing about its row headers and given another.
 *
 * `<Board>` states `hasRowHeaders` from its own `renderRowHeader`, so this is only ever a
 * board composed by hand, where the fact is stated on the provider and the header is drawn
 * by the body. Two places to state one fact is two places to disagree, and neither
 * direction raises anything on its own: the board renders, and what a reader gets is a
 * cell named after a swimlane that was never drawn, or a swimlane drawn that no cell is
 * named after.
 *
 * Development-only, to the console, and per crossing, for the same reasons the report
 * above is. Neither side is coerced to agree with the other: which of the two the caller
 * meant is not something the board can know, and picking one would replace a loud mistake
 * with a quiet one.
 */
function BoardRowHeaderMismatchReport({hasRowHeaders}: {hasRowHeaders: boolean}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error(hasRowHeaders ? ROW_HEADERS_UNDRAWN_MESSAGE : ROW_HEADERS_UNSTATED_MESSAGE)
  }, [hasRowHeaders])

  return null
}

/**
 * A bundler replaces `process.env.NODE_ENV` with a literal, so this is decided at build
 * time and the messages are left unreachable in a production build rather than reached and
 * declined. Which is also why nothing tests the production case: a browser test is built
 * at `test`, and no stub reaches a literal.
 */
const REPORTS_TO_THE_DEVELOPER = process.env.NODE_ENV !== 'production'

const UNBOUNDED_HEIGHT_MESSAGE =
  '[@github-ui/board] This board is taller than the window, so the page is scrolling instead of the board. It owns its scroll containers, so give it a height that fits the viewport. Without one its scrollbars, its auto-scroll, its paging and the windowing of its columns and rows all stop working together.'

const ROW_HEADERS_UNDRAWN_MESSAGE =
  '[@github-ui/board] This board was given `hasRowHeaders` on its `BoardProvider` but no `renderRowHeader` on its `BoardBody`, so every cell is named after a swimlane nobody can see. State the same answer in both places.'

const ROW_HEADERS_UNSTATED_MESSAGE =
  '[@github-ui/board] This board draws a row header for every swimlane but was not given `hasRowHeaders` on its `BoardProvider`, so no cell is named after the swimlane it is in. State the same answer in both places.'

export {defaultBoardLabels} from './labels'

// Re-exported so a caller imports one package rather than two.
export type {
  Anchor,
  BoardCardDescriber,
  BoardCardDetail,
  BoardCell,
  BoardColumn,
  BoardData,
  BoardHeadingLevel,
  BoardItemDragState,
  BoardItemsMove,
  BoardLabels,
  BoardRow,
  BoardSelectedCard,
  BoardSelectionIntent,
  CellAddress,
  CellLabelAddress,
  CellNameParts,
  ColumnKey,
  ItemId,
  RowKey,
  RowNameParts,
} from '@github-ui/board-core'
export {
  BoardProvider,
  type BoardProviderProps,
  boardSelectionKey,
  useBoardSelection,
  type UseBoardSelectionResult,
} from '@github-ui/board-core'
